import React, { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ApiResponse } from "@/types/Api.tsx";
import { Transaction } from "@/types/Transaction.tsx";
import GenericApi from "@/api/genericApi.tsx";
import { ApiRoutes } from "@/config/routes/ApiRoutes.tsx";
import {
  CryptoDepositPostQueryKey,
  TransactionQueryKey,
} from "@/config/constants/QueryKeys.tsx";
import { Loader } from "@/components/index.tsx";
import { TransactionStatus } from "@/types/Status.tsx";
import { getUser, getUserToken } from "@/utils/localStorage";
import { useAppSelector } from "@/store";
import { CryptoDepositPost } from "@/types/BankSettings";
import axios from "axios";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { AnimatePresence, motion } from "framer-motion";
import { isBitramp } from "@/utils/utils";

interface Props {
  title: string;
}

const TableTry: React.FC<Props> = () => {
  const user = getUser();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const wrapperStatusRef = useRef<HTMLDivElement>(null);
  const inboxTransaction = useAppSelector(
    (state) => state.inboxSettings.inboxTransaction
  );
  const [expandedRow, setExpandedRow] = useState<string | null>(
    user?.is_staff ? null : inboxTransaction
  );
  // const [search] = useState<string>("");

  const rowRefs = useRef<{ [key: string]: HTMLTableRowElement | null }>({});

  useEffect(() => {
    if (expandedRow !== null && rowRefs.current[expandedRow]) {
      rowRefs.current[expandedRow]?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [expandedRow]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setIsFilterActionOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperStatusRef.current &&
        !wrapperStatusRef.current.contains(event.target as Node)
      ) {
        setIsFilterStatusOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const route = `${ApiRoutes.TRANSACTIONS}${ApiRoutes.USERS}/${user?.id}`;

  const { data: transactions, isLoading } = useQuery<
    ApiResponse<Transaction[]>
  >({
    queryFn: async () => GenericApi.get<Transaction[]>(route),
    queryKey: [TransactionQueryKey, user?.id],
  });

  const { data: deposits, isLoading: isLoadingDeposits } = useQuery<
    ApiResponse<CryptoDepositPost[]>
  >({
    queryFn: async () =>
      GenericApi.get<CryptoDepositPost[]>(ApiRoutes.CRYPTODEPOSITLIST),
    queryKey: [CryptoDepositPostQueryKey],
  });

  const handleDownloadInvoice = async () => {
    try {
      const response = await axios.get(
        `${GenericApi.baseUrl}/pdf/invoice?transaction_id=${expandedRow}`,
        {
          responseType: "blob",
          headers: {
            "Content-Type": "application/pdf",
            Authorization: `Bearer ${getUserToken()}`,
          },
        }
      );

      const blob = new Blob([response.data], { type: "application/pdf" });
      const linkUrl = window.URL.createObjectURL(blob);

      const downloadLink = document.createElement("a");
      downloadLink.href = linkUrl;
      downloadLink.download = `invoice.pdf`;
      downloadLink.style.display = "none";

      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);

      setTimeout(() => window.URL.revokeObjectURL(linkUrl), 1000);
    } catch (error) {
      console.error("Error downloading PDF:", error);
    }
  };

  const handleDownloadReceipt = async () => {
    try {
      const response = await axios.get(
        `${GenericApi.baseUrl}/pdf/receipt?transaction_id=${expandedRow}`,
        {
          responseType: "blob",
          headers: {
            "Content-Type": "application/pdf",
            Authorization: `Bearer ${getUserToken()}`,
          },
        }
      );

      const blob = new Blob([response.data], { type: "application/pdf" });
      const linkUrl = window.URL.createObjectURL(blob);

      const downloadLink = document.createElement("a");
      downloadLink.href = linkUrl;
      downloadLink.download = `receipt.pdf`;
      downloadLink.style.display = "none";

      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);

      setTimeout(() => window.URL.revokeObjectURL(linkUrl), 1000);
    } catch (error) {
      console.error("Error downloading PDF:", error);
    }
  };

  const combinedData = useMemo(() => {
    if (!transactions || !deposits) return [];
    return [...(transactions?.data || []), ...(deposits?.data || [])].sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
  }, [transactions, deposits]);

  const handleRowClick = (transNo: string) => {
    setExpandedRow((prev) => {
      const newExpandedRow = prev === transNo ? null : transNo;

      if (newExpandedRow !== null) {
        document.addEventListener("click", handleClickOutside);
      }

      return newExpandedRow;
    });
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (!event.target || !(event.target as HTMLElement).closest("table")) {
      setExpandedRow(null);
      document.removeEventListener("click", handleClickOutside);
    }
  };

  const formatDate = (date: string) => {
    if (!date) return;
    const d = new Date(date);
    const pad = (n: number) => n.toString().padStart(2, "0");

    return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
  };

  const formatHour = (date: string) => {
    if (!date) return;
    const d = new Date(date);
    const pad = (n: number) => n.toString().padStart(2, "0");

    return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  };

  const [filterAction, setFilterAction] = useState<string>("");
  const [filterStatus, setFilterStatus] = useState<string>("");
  const [filterDate, setFilterDate] = useState<string>("");
  const [isFilterActionOpen, setIsFilterActionOpen] = useState(false);
  const [isFilterStatusOpen, setIsFilterStatusOpen] = useState(false);

  const filteredData = useMemo(() => {
    const result = combinedData.filter((transaction) => {
      const matchesAction = filterAction
        ? transaction.action === filterAction ||
          ("type" in transaction && transaction.type === filterAction)
        : true;

      const matchesStatus = filterStatus
        ? transaction.status === filterStatus
        : true;

      const matchesDate = filterDate
        ? formatDate(transaction.created_at) === filterDate
        : true;

      return matchesAction && matchesStatus && matchesDate;
    });
    return result;
  }, [combinedData, filterAction, filterStatus, filterDate]);

  // const renderTransactions = () => {
  //   if (search) {
  //     return transactions?.data?.filter((transaction) => {
  //       return (
  //         transaction.id.toString().includes(search) ||
  //         transaction?.action?.includes(search) ||
  //         transaction?.status?.includes(search)
  //       );
  //     });
  //   }

  //   return transactions?.data?.filter((transaction) => {
  //     return (
  //       transaction.id.toString().includes(search) ||
  //       transaction?.action?.includes(search) ||
  //       transaction?.status?.includes(search)
  //     );
  //   });
  // };

  // const transactionsData = renderTransactions();

  if (isLoading || isLoadingDeposits) {
    return <Loader />;
  }

  return (
    <div className="tableContainer !w-full !p-0 ">
      <div className="flex justify-between items-center px-8 pt-8 pb-3">
        <div className="Heading2 dark-blue bold ">Transactions</div>
        <div className="flex h-fit">
          {/* <select
            value={filterAction}
            onChange={(e) => setFilterAction(e.target.value)}
            className="px-4 py-1 mr-4 rounded-[0.625rem] bg-[#ffffff] border-[#E8E8E8] text-[#737791] pshadow"
          >
            <option value="">All Actions</option>
            <option value="buy">buy</option>
            <option value="deposit">deposit</option>
          </select> */}

          <div className="relative mr-4 !z-[999999]" ref={wrapperRef}>
            <div
              onClick={() => setIsFilterActionOpen(!isFilterActionOpen)}
              className={`px-4 py-2.5 rounded-[0.625rem] ${
                isBitramp() ? "bg-[#2b2b2b] text-white" : "bg-white"
              }  border-none focus:ring-0 pshadow cursor-pointer flex items-center justify-between gap-2 w-[150px]`}
            >
              {filterAction === ""
                ? "All Actions"
                : filterAction.charAt(0).toUpperCase() + filterAction.slice(1)}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="11"
                height="10"
                viewBox="0 0 11 10"
                fill="none"
              >
                <path
                  d="M6.38133 9.35606C6.00426 10.0594 4.99573 10.0594 4.61867 9.35606L0.660254 1.97249C0.303111 1.30632 0.785718 0.5 1.54159 0.5L9.45841 0.500001C10.2143 0.500001 10.6969 1.30632 10.3397 1.97249L6.38133 9.35606Z"
                  fill={isBitramp() ? "#fff" : "#737791"}
                />
              </svg>
            </div>

            <AnimatePresence>
              {isFilterActionOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.2 }}
                  className={`${
                    isBitramp()
                      ? "absolute top-full left-0 w-full bg-[#2b2b2b] border border-[rgba(255,255,255,0.1)] crypto-list_bitramp text-white rounded-[0.625rem] shadow-md mt-1 z-10 max-h-60 overflow-y-auto"
                      : "absolute top-full left-0 w-full bg-white rounded-[0.625rem] shadow-md mt-1 z-10 max-h-60 overflow-y-auto"
                  }`}
                >
                  <div
                    onClick={() => {
                      setFilterAction("");
                      setIsFilterActionOpen(false);
                    }}
                    className={`${
                      isBitramp()
                        ? "px-4 py-2 hover:bg-[#262626] cursor-pointer"
                        : "px-4 py-2 hover:bg-gray-100 cursor-pointer"
                    }`}
                  >
                    All Actions
                  </div>
                  <div
                    onClick={() => {
                      setFilterAction("buy");
                      setIsFilterActionOpen(false);
                    }}
                    className={`${
                      isBitramp()
                        ? "px-4 py-2 hover:bg-[#262626] cursor-pointer"
                        : "px-4 py-2 hover:bg-gray-100 cursor-pointer"
                    }`}
                  >
                    Buy
                  </div>
                  <div
                    onClick={() => {
                      setFilterAction("deposit");
                      setIsFilterActionOpen(false);
                    }}
                    className={`${
                      isBitramp()
                        ? "px-4 py-2 hover:bg-[#262626] cursor-pointer"
                        : "px-4 py-2 hover:bg-gray-100 cursor-pointer"
                    }`}
                  >
                    Deposit
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-1 mr-4 rounded-[0.625rem] bg-[#ffffff] border-[#E8E8E8] text-[#737791] pshadow"
          >
            <option value="">All Statuses</option>
            <option value="completed">Completed</option>
            <option value="pending">Pending</option>
            <option value="declined">Declined</option>
          </select> */}

          <div className="relative mr-4 !z-[999999]" ref={wrapperStatusRef}>
            <div
              onClick={() => setIsFilterStatusOpen(!isFilterStatusOpen)}
              className={`px-4 py-2.5 rounded-[0.625rem] ${
                isBitramp() ? "bg-[#2b2b2b] text-white" : "bg-white"
              }  border-none focus:ring-0 pshadow cursor-pointer flex items-center justify-between gap-2 w-[150px]`}
            >
              {filterStatus === ""
                ? "All Statuses"
                : filterStatus.charAt(0).toUpperCase() + filterStatus.slice(1)}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="11"
                height="10"
                viewBox="0 0 11 10"
                fill="none"
              >
                <path
                  d="M6.38133 9.35606C6.00426 10.0594 4.99573 10.0594 4.61867 9.35606L0.660254 1.97249C0.303111 1.30632 0.785718 0.5 1.54159 0.5L9.45841 0.500001C10.2143 0.500001 10.6969 1.30632 10.3397 1.97249L6.38133 9.35606Z"
                  fill={isBitramp() ? "#fff" : "#737791"}
                />
              </svg>
            </div>

            <AnimatePresence>
              {isFilterStatusOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.2 }}
                  className={`${
                    isBitramp()
                      ? "absolute top-full left-0 w-full bg-[#2b2b2b] border border-[rgba(255,255,255,0.1)] crypto-list_bitramp text-white rounded-[0.625rem] shadow-md mt-1 z-10 max-h-60 overflow-y-auto"
                      : "absolute top-full left-0 w-full bg-white rounded-[0.625rem] shadow-md mt-1 z-10 max-h-60 overflow-y-auto"
                  }`}
                >
                  <div
                    onClick={() => {
                      setFilterStatus("");
                      setIsFilterStatusOpen(false);
                    }}
                    className={`${
                      isBitramp()
                        ? "px-4 py-2 hover:bg-[#262626] cursor-pointer"
                        : "px-4 py-2 hover:bg-gray-100 cursor-pointer"
                    }`}
                  >
                    All Statuses
                  </div>
                  <div
                    onClick={() => {
                      setFilterStatus("completed");
                      setIsFilterStatusOpen(false);
                    }}
                    className={`${
                      isBitramp()
                        ? "px-4 py-2 hover:bg-[#262626] cursor-pointer"
                        : "px-4 py-2 hover:bg-gray-100 cursor-pointer"
                    }`}
                  >
                    Completed
                  </div>
                  <div
                    onClick={() => {
                      setFilterStatus("pending");
                      setIsFilterStatusOpen(false);
                    }}
                    className={`${
                      isBitramp()
                        ? "px-4 py-2 hover:bg-[#262626] cursor-pointer"
                        : "px-4 py-2 hover:bg-gray-100 cursor-pointer"
                    }`}
                  >
                    Pending
                  </div>
                  <div
                    onClick={() => {
                      setFilterStatus("declined");
                      setIsFilterStatusOpen(false);
                    }}
                    className={`${
                      isBitramp()
                        ? "px-4 py-2 hover:bg-[#262626] cursor-pointer"
                        : "px-4 py-2 hover:bg-gray-100 cursor-pointer"
                    }`}
                  >
                    Declined
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* <input
            type="date"
            value={filterDate}
            onChange={(e) => setFilterDate(e.target.value)}
            className="px-4 py-1 rounded-[0.625rem] bg-[#ffffff] border-[#E8E8E8] text-[#737791] pshadow"
          /> */}
          <DatePicker
            className="m-0"
            popperClassName="z-[9999999999]"
            popperPlacement="bottom-start"
            selected={filterDate ? new Date(filterDate) : null}
            onChange={(date) =>
              setFilterDate(date ? date.toISOString().split("T")[0] : "")
            }
            customInput={
              <button
                className={`${
                  isBitramp() ? "bg-[#2b2b2b]" : "bg-[#1D2746]"
                } rounded-[0.625rem] py-4 px-[1rem] text-white flex items-center justify-center z-[10000000]`}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="12"
                  height="14"
                  viewBox="0 0 17 19"
                  fill="none"
                >
                  <g clip-path="url(#clip0_4516_12445)">
                    <path
                      d="M11.9445 2.0899C11.9445 2.03286 11.9445 1.98681 11.9445 1.94051C11.9445 1.53124 11.9428 1.12197 11.945 0.712693C11.9467 0.387321 12.127 0.127432 12.4102 0.0327878C12.8201 -0.104063 13.2544 0.203915 13.2654 0.651046C13.2755 1.06006 13.2684 1.46959 13.2686 1.87887C13.2686 1.94256 13.2686 2.00625 13.2686 2.08632C13.3255 2.09015 13.3764 2.0968 13.4273 2.0968C14.1819 2.09757 14.9363 2.09885 15.6908 2.09629C15.9484 2.09553 16.1869 2.1605 16.4016 2.30656C16.7828 2.56593 16.9791 2.93812 16.9946 3.40878C17.0039 3.69399 16.998 3.97997 16.998 4.2657C16.998 8.71219 16.998 13.1587 16.9985 17.6052C16.9985 18.0549 16.8416 18.4273 16.5025 18.7105C16.3232 18.8604 16.117 18.9525 15.8884 18.9824C15.787 18.9957 15.684 18.999 15.5816 18.999C13.3469 18.9998 11.1121 18.999 8.87742 18.999C6.37205 18.999 3.86692 18.9983 1.36156 19C1.04368 19 0.751636 18.9243 0.499942 18.7171C0.186987 18.4593 0.0159926 18.1229 0.00221463 17.705C0.000984461 17.6666 0.00172256 17.6282 0.00172256 17.5898C0.00172256 15.7097 0.00172256 13.8296 0.00172256 11.9498C0.00147653 9.12326 0.00221463 6.29697 3.22586e-07 3.47043C-0.000245712 3.05322 0.14024 2.70483 0.438926 2.42806C0.647071 2.23519 0.892859 2.12622 1.17162 2.10243C1.23288 2.09732 1.29463 2.09732 1.3559 2.09732C1.9914 2.0968 2.62716 2.0968 3.26266 2.0968C3.3072 2.0968 3.35173 2.0968 3.41078 2.0968C3.41348 2.04206 3.41742 1.99704 3.41742 1.95228C3.41963 1.51743 3.41791 1.08283 3.42382 0.647977C3.42849 0.31314 3.69667 0.0258814 4.01184 0.00618508C4.37253 -0.0163249 4.67589 0.219007 4.72608 0.565099C4.73887 0.653093 4.74109 0.743389 4.74133 0.832661C4.74232 1.19922 4.74059 1.56577 4.7401 1.93258C4.7401 1.98272 4.7401 2.03311 4.7401 2.0899H11.9442H11.9445ZM1.00751 7.13189V7.30481C1.00751 8.94139 1.00751 10.5782 1.00751 12.2148C1.00751 13.8302 1.0085 15.4458 1.00653 17.0611C1.00628 17.2788 1.06213 17.4747 1.18933 17.6448C1.38124 17.9014 1.63859 18.014 1.95007 18.014C6.36541 18.0127 10.7807 18.0129 15.1961 18.0129C15.2249 18.0129 15.2536 18.0142 15.2822 18.0117C15.5649 17.9861 15.7765 17.8474 15.9106 17.5845C15.9799 17.4481 15.9932 17.3013 15.993 17.1506C15.9925 13.8816 15.9927 10.6122 15.9927 7.34318C15.9927 7.30046 15.9942 7.25774 15.9917 7.21528C15.99 7.18714 15.9822 7.15951 15.977 7.13163H1.00751V7.13189ZM3.41816 3.0059C3.12636 3.22921 2.92806 3.73211 3.11529 4.22093C3.26291 4.60642 3.65853 4.94611 4.19538 4.89316C4.62077 4.85121 5.00582 4.48773 5.0939 4.03548C5.17091 3.64053 5.00631 3.18803 4.74256 3.02355C4.74256 3.06601 4.74256 3.10362 4.74256 3.14096C4.74207 3.36683 4.74379 3.5927 4.74059 3.81857C4.7337 4.27465 4.35752 4.59388 3.93754 4.50205C3.64205 4.43734 3.42898 4.17668 3.42037 3.85668C3.41422 3.62237 3.41865 3.38781 3.4184 3.1535C3.4184 3.11257 3.4184 3.07164 3.4184 3.0059H3.41816ZM11.9445 3.02458C11.6945 3.17345 11.496 3.65025 11.6027 4.08127C11.7196 4.55372 12.1214 4.88933 12.5856 4.897C13.0671 4.90519 13.48 4.58672 13.6099 4.10403C13.727 3.66893 13.5368 3.18522 13.2768 3.02432C13.2743 3.0655 13.2704 3.10157 13.2701 3.13764C13.2691 3.38064 13.2755 3.62365 13.2667 3.8664C13.2492 4.35164 12.7539 4.66141 12.3317 4.4532C12.0773 4.3276 11.9531 4.10941 11.946 3.82087C11.9405 3.59935 11.9445 3.37757 11.9445 3.15605C11.9445 3.11462 11.9445 3.07343 11.9445 3.02483V3.02458Z"
                      fill="white"
                    />
                    <path
                      d="M3.92676 14.7373C4.12752 14.7373 4.32829 14.7365 4.52905 14.7373C4.79944 14.7383 4.94091 14.8821 4.94165 15.1599C4.94288 15.5817 4.94313 16.0035 4.94165 16.425C4.94091 16.6859 4.79526 16.8353 4.54234 16.8361C4.13269 16.8374 3.7228 16.8312 3.31315 16.8389C3.1114 16.8427 2.9131 16.6944 2.91605 16.4204C2.92048 15.9986 2.91949 15.5768 2.91605 15.1553C2.91383 14.8688 3.12198 14.7237 3.32447 14.735C3.52474 14.7462 3.72599 14.7375 3.92676 14.7375V14.7373Z"
                      fill="white"
                    />
                    <path
                      d="M3.92968 10.3986C3.72473 10.3986 3.51979 10.393 3.31509 10.4001C3.09759 10.4078 2.91528 10.2541 2.91602 9.98522C2.91725 9.55932 2.91503 9.13342 2.91749 8.70752C2.91897 8.45403 3.06659 8.29979 3.30869 8.29851C3.72252 8.29646 4.13635 8.2962 4.54993 8.29851C4.79154 8.30004 4.9399 8.45505 4.94113 8.70752C4.94334 9.13777 4.9426 9.56802 4.94088 9.99801C4.9399 10.2433 4.79474 10.3942 4.55633 10.3978C4.34745 10.4009 4.13856 10.3986 3.92943 10.3986H3.92968Z"
                      fill="white"
                    />
                    <path
                      d="M3.93607 13.5645C3.72719 13.5645 3.51781 13.5578 3.30918 13.5662C3.1089 13.5744 2.91626 13.4209 2.91798 13.1562C2.92069 12.7303 2.91626 12.3041 2.91921 11.8782C2.92118 11.6163 3.06855 11.4667 3.31852 11.4659C3.72817 11.4646 4.13807 11.4644 4.54771 11.4659C4.79227 11.4669 4.94063 11.6161 4.94235 11.8693C4.94555 12.3036 4.94235 12.7382 4.94432 13.1728C4.9453 13.3767 4.79449 13.5724 4.56297 13.5657C4.35408 13.5596 4.1452 13.5645 3.93607 13.5645Z"
                      fill="white"
                    />
                    <path
                      d="M7.9853 12.5016C7.9853 12.7187 7.98456 12.9362 7.9853 13.1533C7.98604 13.3879 7.83989 13.5526 7.61379 13.5549C7.18765 13.559 6.76152 13.5595 6.33539 13.5549C6.11445 13.5524 5.96683 13.3943 5.96535 13.1643C5.96216 12.7256 5.96216 12.2867 5.96486 11.848C5.96634 11.6081 6.11913 11.4533 6.35581 11.45C6.61808 11.4464 6.88036 11.4495 7.14263 11.4495C7.28607 11.4495 7.42951 11.4525 7.57294 11.4487C7.83866 11.4418 8.00646 11.6388 7.98407 11.8754C7.98333 11.8838 7.98407 11.8925 7.98407 11.9009C7.98407 12.1012 7.98407 12.3013 7.98407 12.5016H7.9853Z"
                      fill="white"
                    />
                    <path
                      d="M12.0569 9.35261C12.0569 9.14388 12.0564 8.93515 12.0569 8.72642C12.0578 8.44581 12.1973 8.29822 12.4638 8.29745C12.8611 8.29668 13.2587 8.29668 13.6561 8.29745C13.9329 8.29796 14.0753 8.44351 14.0761 8.72744C14.0773 9.1449 14.0773 9.56236 14.0761 9.97982C14.0753 10.2535 13.9218 10.4042 13.6613 10.4021C13.2639 10.3991 12.8666 10.3998 12.4692 10.4029C12.2136 10.4047 12.0586 10.254 12.0574 9.99133C12.0564 9.77825 12.0574 9.56543 12.0574 9.35235H12.0571L12.0569 9.35261Z"
                      fill="white"
                    />
                    <path
                      d="M10.0181 16.836C9.80922 16.836 9.6001 16.8301 9.39121 16.8375C9.1683 16.8455 9.00371 16.6477 9.00789 16.4336C9.01626 15.9993 9.0106 15.5647 9.00986 15.1301C9.00986 14.959 9.08391 14.8362 9.23326 14.7671C9.27656 14.7472 9.32823 14.7387 9.3762 14.7385C9.80652 14.7364 10.2366 14.7349 10.6669 14.7385C10.8817 14.7403 11.0266 14.894 11.0283 15.1206C11.0313 15.568 11.0313 16.0152 11.0276 16.4625C11.0259 16.6792 10.8775 16.8314 10.6696 16.8347C10.4526 16.8383 10.2354 16.8355 10.0181 16.8355V16.836Z"
                      fill="white"
                    />
                    <path
                      d="M7.98354 9.35387C7.98354 9.57104 7.98502 9.78847 7.98305 10.0056C7.98108 10.2356 7.85216 10.3796 7.63171 10.3988C7.583 10.4031 7.53355 10.3988 7.48458 10.3991C7.11184 10.4001 6.73885 10.4001 6.36611 10.4029C6.1277 10.4047 5.96631 10.2502 5.96483 10.0038C5.96212 9.56925 5.96237 9.13491 5.96483 8.70031C5.96631 8.45475 6.11368 8.29999 6.34938 8.2982C6.76739 8.29513 7.18516 8.29513 7.60317 8.2982C7.8433 8.29999 7.98699 8.46268 7.98477 8.71464C7.98305 8.92771 7.98453 9.14054 7.98453 9.35361H7.98354V9.35387Z"
                      fill="white"
                    />
                    <path
                      d="M9.00985 12.5135C9.00985 12.3004 9.016 12.0873 9.00813 11.8745C8.99902 11.6282 9.18822 11.4604 9.39809 11.4639C9.81168 11.4708 10.2258 11.4711 10.6393 11.4639C10.8736 11.4598 11.0335 11.6519 11.0305 11.8602C11.0244 12.2988 11.0281 12.7375 11.0288 13.1762C11.0288 13.3425 10.9587 13.4635 10.814 13.5315C10.7673 13.5535 10.7114 13.5625 10.6598 13.563C10.2336 13.5653 9.80749 13.5602 9.38161 13.5661C9.16879 13.5691 9.00296 13.3752 9.00911 13.1778C9.016 12.9565 9.01059 12.7347 9.01059 12.5132H9.00985V12.5135Z"
                      fill="white"
                    />
                    <path
                      d="M13.061 13.5641C12.8563 13.5641 12.6511 13.5566 12.4467 13.5661C12.2528 13.5751 12.0503 13.4218 12.0548 13.1586C12.0621 12.7286 12.0609 12.2984 12.0555 11.8681C12.0521 11.5972 12.2568 11.4596 12.4499 11.4629C12.8595 11.4701 13.2692 11.464 13.6788 11.4653C13.9305 11.466 14.0754 11.6164 14.0762 11.8778C14.0777 12.2994 14.0708 12.7212 14.0789 13.1428C14.0831 13.3627 13.9426 13.5835 13.6631 13.5653C13.561 13.5587 13.4584 13.5641 13.356 13.5641C13.2576 13.5641 13.1595 13.5641 13.061 13.5641Z"
                      fill="white"
                    />
                    <path
                      d="M6.9776 14.7371C7.18255 14.7371 7.38725 14.7361 7.5922 14.7374C7.83429 14.7389 7.98044 14.8806 7.98265 15.1282C7.98659 15.5713 7.98659 16.0141 7.98339 16.4571C7.98167 16.6809 7.83356 16.8339 7.61803 16.8354C7.18796 16.8385 6.75765 16.8387 6.32758 16.8352C6.11402 16.8334 5.96714 16.6768 5.96542 16.4522C5.96222 16.0092 5.96197 15.5664 5.96517 15.1234C5.96689 14.8926 6.11525 14.7417 6.3384 14.7381C6.55147 14.7346 6.76454 14.7374 6.9776 14.7374V14.7371Z"
                      fill="white"
                    />
                    <path
                      d="M11.0286 9.36611C11.0286 9.57484 11.0301 9.78357 11.0281 9.9923C11.0259 10.2509 10.8874 10.3967 10.6396 10.3977C10.2258 10.3998 9.81218 10.4 9.39835 10.3977C9.15158 10.3965 9.01134 10.2476 9.0106 9.99128C9.00962 9.56538 9.01577 9.13948 9.00789 8.71358C9.00396 8.49718 9.1437 8.2951 9.40549 8.29689C9.81513 8.2997 10.2248 8.29944 10.6344 8.29689C10.8972 8.2951 11.0392 8.49871 11.0308 8.7146C11.0224 8.93152 11.0288 9.14894 11.0288 9.36611H11.0286Z"
                      fill="white"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_4516_12445">
                      <rect width="17" height="19" fill="white" />
                    </clipPath>
                  </defs>
                </svg>
              </button>
            }
          />
        </div>
      </div>
      <div className="w-full h-[1.3rem] Container !rounded-b-[0rem] flex items-center justify-center"></div>
      <div
        className={`overflow-x-auto tableContainer--background ${
          isBitramp() ? "crypto-list_bitramp" : "crypto-list"
        } Container !rounded-t-[0rem] Text dark-blue max-h-[70vh] pshadow mb-12 px-4`}
      >
        <table className="w-full border-collapse relative">
          <thead className="sticky top-0 z-[9999] tableContainer__header">
            <tr>
              <th
                className={`${
                  isBitramp() ? "bg-[#EE33D9]" : "bg-[#202a43]"
                } tableContainer__header__row white medium`}
              >
                ACTION
              </th>
              <th
                className={`${
                  isBitramp() ? "bg-[#EE33D9]" : "bg-[#202a43]"
                } tableContainer__header__row white medium`}
              >
                AMOUNT
              </th>
              <th
                className={`${
                  isBitramp() ? "bg-[#EE33D9]" : "bg-[#202a43]"
                } tableContainer__header__row white medium`}
              >
                PURCHASED
              </th>
              <th
                className={`${
                  isBitramp() ? "bg-[#EE33D9]" : "bg-[#202a43]"
                } tableContainer__header__row white medium`}
              >
                DATE
              </th>
              <th
                className={`${
                  isBitramp() ? "bg-[#EE33D9]" : "bg-[#202a43]"
                } tableContainer__header__row white medium`}
              >
                STATUS
              </th>
              <th
                className={`${
                  isBitramp() ? "bg-[#EE33D9]" : "bg-[#202a43]"
                } tableContainer__header__row !pe-8 white medium`}
              >
                TYPE
              </th>
            </tr>
          </thead>
          <tbody
            key={filterAction + filterStatus + filterDate}
            className="tableContainer__body"
          >
            {filteredData && filteredData.length > 0 ? (
              filteredData.map((transaction, index) => {
                const amount = Number(transaction.amount);
                const isEven = index % 2 === 1;

                return (
                  <React.Fragment key={transaction.id}>
                    <tr
                      ref={(el) => (rowRefs.current[transaction.id] = el)}
                      onClick={() => {
                        if (!transaction.action) {
                          handleRowClick(transaction.id);
                        }
                      }}
                    >
                      <td
                        className={`w-[10%] ${
                          expandedRow === transaction.id
                            ? "tableContainer__body__row__noRad"
                            : "tableContainer__body__row"
                        } ${isEven ? "par" : "impar"} font-[700] uppercase`}
                      >
                        {`action` in transaction
                          ? transaction.action
                          : (transaction as Transaction).type}
                      </td>
                      <td
                        className={`w-[20%]  ${
                          expandedRow === transaction.id
                            ? "tableContainer__body__row__noRad"
                            : "tableContainer__body__row"
                        } ${isEven ? "par" : ""} font-[700]`}
                      >
                        {Number(transaction.amount).toFixed(
                          (`rate` in transaction
                            ? transaction.rate.from_currency_symbol
                            : transaction.currency) &&
                            ["BTC", "ETH"].includes(
                              `rate` in transaction
                                ? transaction.rate.from_currency_symbol
                                : transaction.currency
                            )
                            ? 8
                            : 2
                        )}{" "}
                        <span className="low_caps">
                          {`rate` in transaction
                            ? transaction.rate.from_currency_symbol
                            : transaction.currency}
                        </span>
                      </td>
                      <td
                        className={` w-[20%]  ${
                          expandedRow === transaction.id
                            ? "tableContainer__body__row__noRad"
                            : "tableContainer__body__row"
                        } ${isEven ? "par" : "impar"}  font-[700]`}
                      >
                        {(`rate` in transaction
                          ? transaction.rate.rate * amount
                          : 0
                        ).toFixed(2)}{" "}
                        <span className="low_caps">
                          {`rate` in transaction
                            ? transaction.rate.to_currency_symbol
                            : ""}
                        </span>
                      </td>
                      <td
                        className={` w-[20%] low_caps ${
                          expandedRow === transaction.id
                            ? "tableContainer__body__row__noRad"
                            : "tableContainer__body__row"
                        } ${isEven ? "par" : "impar"}`}
                      >
                        {formatDate(transaction.created_at)}{" "}
                        <span className="text-[#b3b2b1]">
                          {formatHour(transaction.created_at)}
                        </span>
                      </td>
                      <td
                        style={{
                          color:
                            transaction?.status === TransactionStatus.COMPLETED
                              ? "#2DDE24"
                              : "#F00",
                        }}
                        className={`low_caps ${
                          expandedRow === transaction.id
                            ? "tableContainer__body__row__noRad"
                            : "tableContainer__body__row"
                        } ${isEven ? "par" : "impar"}`}
                      >
                        {transaction?.status ?? "N/A"}
                      </td>
                      <td
                        className={` relative !pe-8 ${
                          expandedRow === transaction.id
                            ? "tableContainer__body__row__noRad"
                            : "tableContainer__body__row"
                        } ${isEven ? "par" : "impar"}  font-[700]`}
                      >
                        <span className="low_caps">
                          {`rate` in transaction ? (
                            transaction.client != null ? (
                              <div className="flex justify-center items-center gap-2">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="24"
                                  height="25"
                                  viewBox="0 0 24 25"
                                  fill="none"
                                >
                                  <rect
                                    width="24"
                                    height="25"
                                    rx="4"
                                    fill="#ECE488"
                                  />
                                  <path
                                    d="M6.6428 5.80805L18.0548 5.79883C18.6366 5.85345 19.1123 6.21522 19.2004 6.7742V19.8647C19.1495 20.1563 18.8161 20.2999 18.5613 20.1105L17.2246 18.8532L17.1696 18.8691L15.8754 20.0946C15.7117 20.212 15.565 20.2386 15.3847 20.1307L14.0318 18.8532L13.9768 18.8691L12.6826 20.0946C12.4937 20.2247 12.3044 20.2261 12.1177 20.0946L10.8017 18.8532L10.7467 18.8691L9.41564 20.1307C9.235 20.2389 9.08823 20.2116 8.9249 20.0946L7.60885 18.8532L7.55391 18.8691L6.23899 20.1109C5.98421 20.2999 5.65078 20.1566 5.59998 19.8651V6.7742C5.68013 6.25069 6.10539 5.9063 6.6428 5.80805ZM18.41 18.8879V6.88202C18.41 6.87528 18.363 6.77242 18.3536 6.75824C18.3227 6.71142 18.2046 6.61388 18.1492 6.5965H6.65183C6.59651 6.61353 6.47834 6.71106 6.44748 6.75824C6.43807 6.77278 6.39103 6.87528 6.39103 6.88202V18.8879L7.36875 17.9846C7.50047 17.8994 7.71347 17.9023 7.83804 18.0005L9.18946 19.2781L10.5409 18.0005C10.7087 17.8799 10.9533 17.903 11.1058 18.036L12.3627 19.2259L12.4177 19.2419L13.6965 18.036C13.8489 17.9033 14.0924 17.8796 14.2613 18.0005L15.6127 19.2781L16.9642 18.0005C17.0887 17.9019 17.3014 17.8994 17.4335 17.9846L18.4112 18.8879H18.41Z"
                                    fill="#202A43"
                                  />
                                  <path
                                    d="M15.5929 11.0501C15.1398 10.9852 14.7131 10.6117 14.5339 10.2237C14.1418 9.37421 14.6175 8.34529 15.5922 8.14099C15.5817 7.85867 15.5387 7.35963 15.9482 7.32487C16.4615 7.28125 16.4401 7.79447 16.4201 8.14099C16.8804 8.2098 17.3173 8.59498 17.4904 8.99258C17.5525 9.13481 17.6334 9.4299 17.6229 9.57957C17.5996 9.91404 17.2706 10.0839 16.9688 9.91049C16.7502 9.78493 16.8307 9.45756 16.728 9.25114C16.6693 9.13303 16.5526 9.0018 16.4194 8.95711V10.4113C16.8725 10.4762 17.2992 10.8497 17.4784 11.2377C17.8705 12.0872 17.3948 13.1161 16.4201 13.3204C16.4307 13.6027 16.4736 14.1017 16.0641 14.1365C15.5508 14.1801 15.5722 13.6669 15.5922 13.3204C15.1319 13.2516 14.695 12.8664 14.5219 12.4688C14.4598 12.3266 14.3789 12.0315 14.3894 11.8818C14.4128 11.5473 14.7417 11.3774 15.0435 11.5509C15.2621 11.6764 15.1816 12.0038 15.2843 12.2102C15.3431 12.3283 15.4597 12.4596 15.5929 12.5043V11.0501ZM15.5929 10.2343V8.95747C15.0781 9.19297 15.08 10.0006 15.5929 10.2343ZM16.4194 12.5043C16.9342 12.2688 16.9323 11.4611 16.4194 11.2274V12.5043Z"
                                    fill="#202A43"
                                  />
                                  <path
                                    d="M7.54351 9.21182L13.2454 9.20508C13.7327 9.32283 13.7338 9.86691 13.2454 9.98537L7.57437 9.98466C7.06857 9.9233 7.05766 9.28949 7.54351 9.21182Z"
                                    fill="#202A43"
                                  />
                                  <path
                                    d="M7.54351 13.7528L13.2454 13.7461C13.7327 13.8638 13.7338 14.4079 13.2454 14.5264L7.57437 14.5257C7.06857 14.4643 7.05766 13.8305 7.54351 13.7528Z"
                                    fill="#202A43"
                                  />
                                  <path
                                    d="M7.46748 10.7361L13.283 10.7305C13.6913 10.8493 13.7214 11.327 13.3214 11.476L7.49872 11.4753C7.08776 11.3508 7.08325 10.8989 7.46748 10.7365V10.7361Z"
                                    fill="#202A43"
                                  />
                                  <path
                                    d="M7.46749 12.2626L13.3214 12.2559C13.7226 12.4055 13.6902 12.8826 13.283 13.0014H7.49872C7.10207 12.8741 7.07008 12.4179 7.46749 12.2626Z"
                                    fill="#202A43"
                                  />
                                  <path
                                    d="M7.46749 7.72247L10.5038 7.7168C10.9158 7.82959 10.8842 8.37969 10.4661 8.46162H7.49872C7.10207 8.33429 7.07008 7.87818 7.46749 7.72283V7.72247Z"
                                    fill="#202A43"
                                  />
                                </svg>
                                POS
                              </div>
                            ) : (
                              <div className="flex justify-center items-center gap-2">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="24"
                                  height="25"
                                  viewBox="0 0 24 25"
                                  fill="none"
                                >
                                  <rect
                                    width="24"
                                    height="25"
                                    rx="4"
                                    fill="#202A43"
                                  />
                                  <path
                                    d="M5.57818 5.05787C6.21891 4.99129 7.39636 4.97899 8.03564 5.03761C8.84291 5.11142 9.45236 5.6614 9.59418 6.4538V19.5462C9.46254 20.3024 8.87345 20.869 8.10255 20.9566C7.38255 21.0383 5.78473 21.0036 5.08436 20.8552C4.54836 20.7416 4.06909 20.0723 4 19.5469V6.4538C4.13018 5.85968 4.52073 5.28148 5.13964 5.12806C5.24364 5.10201 5.46545 5.06945 5.57818 5.05787ZM5.30618 7.93439H8.25164C8.40073 7.93439 8.40073 7.42784 8.25164 7.42784H5.34255C5.17164 7.42784 5.25673 7.82078 5.30618 7.93439ZM6.52145 16.1993C5.66545 16.3462 5.31127 17.4599 5.85673 18.1336C6.49309 18.9195 7.86109 18.6061 7.99491 17.5923C8.11491 16.6848 7.42764 16.0437 6.52218 16.1993H6.52145Z"
                                    fill="white"
                                  />
                                  <path
                                    d="M10.397 17.7771V16.1851H18.397V8.22485H10.397V6.63281H18.6515C18.6719 6.63281 18.9664 6.73774 19.0304 6.76235C19.6086 6.98451 19.8762 7.46501 19.9933 8.04756L19.9999 16.2241C19.9664 16.6779 19.7562 17.2177 19.3679 17.4768C19.2631 17.547 18.7424 17.7764 18.6508 17.7764H10.3962L10.397 17.7771Z"
                                    fill="white"
                                  />
                                  <path
                                    d="M14.397 18.5723V19.3683H16.797V20.9603H10.397V19.3683L11.197 19.369V18.5723H14.397Z"
                                    fill="white"
                                  />
                                </svg>
                                Web
                              </div>
                            )
                          ) : null}
                        </span>
                        <div className="absolute right-4 top-1/2 -translate-y-1/2">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="12"
                            height="10"
                            viewBox="0 0 12 10"
                            fill="none"
                          >
                            <path
                              d="M6.86603 9.5C6.48112 10.1667 5.51887 10.1667 5.13397 9.5L0.803847 2C0.418947 1.33333 0.900073 0.5 1.66987 0.5L10.3301 0.500001C11.0999 0.500001 11.5811 1.33333 11.1962 2L6.86603 9.5Z"
                              fill={isBitramp() ? "#fff" : "#222B45"}
                            />
                          </svg>
                        </div>
                      </td>
                    </tr>
                    {expandedRow === transaction.id && (
                      <tr className="expandedRow">
                        <td
                          className={`expandedRow__cell ${
                            isEven ? "par" : "impar"
                          }`}
                          colSpan={6}
                        >
                          <div className="flex flex-col gap-3">
                            <button className="flex-space-100">
                              <div></div>
                              <div
                                onClick={() =>
                                  `rate` in transaction
                                    ? transaction.client != null
                                      ? handleDownloadReceipt()
                                      : handleDownloadInvoice()
                                    : null
                                }
                                className="font-[500] flex gap-2 cursor-pointer"
                              >
                                <div>
                                  {`rate` in transaction
                                    ? transaction.client != null
                                      ? transaction.client.first_name
                                      : user?.first_name
                                    : user?.first_name}{" "}
                                </div>
                                {`rate` in transaction ? (
                                  transaction.client != null ? (
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="24"
                                      height="25"
                                      viewBox="0 0 24 25"
                                      fill="none"
                                    >
                                      <rect
                                        width="24"
                                        height="25"
                                        rx="4"
                                        fill="#ECE488"
                                      />
                                      <path
                                        d="M6.6428 5.80805L18.0548 5.79883C18.6366 5.85345 19.1123 6.21522 19.2004 6.7742V19.8647C19.1495 20.1563 18.8161 20.2999 18.5613 20.1105L17.2246 18.8532L17.1696 18.8691L15.8754 20.0946C15.7117 20.212 15.565 20.2386 15.3847 20.1307L14.0318 18.8532L13.9768 18.8691L12.6826 20.0946C12.4937 20.2247 12.3044 20.2261 12.1177 20.0946L10.8017 18.8532L10.7467 18.8691L9.41564 20.1307C9.235 20.2389 9.08823 20.2116 8.9249 20.0946L7.60885 18.8532L7.55391 18.8691L6.23899 20.1109C5.98421 20.2999 5.65078 20.1566 5.59998 19.8651V6.7742C5.68013 6.25069 6.10539 5.9063 6.6428 5.80805ZM18.41 18.8879V6.88202C18.41 6.87528 18.363 6.77242 18.3536 6.75824C18.3227 6.71142 18.2046 6.61388 18.1492 6.5965H6.65183C6.59651 6.61353 6.47834 6.71106 6.44748 6.75824C6.43807 6.77278 6.39103 6.87528 6.39103 6.88202V18.8879L7.36875 17.9846C7.50047 17.8994 7.71347 17.9023 7.83804 18.0005L9.18946 19.2781L10.5409 18.0005C10.7087 17.8799 10.9533 17.903 11.1058 18.036L12.3627 19.2259L12.4177 19.2419L13.6965 18.036C13.8489 17.9033 14.0924 17.8796 14.2613 18.0005L15.6127 19.2781L16.9642 18.0005C17.0887 17.9019 17.3014 17.8994 17.4335 17.9846L18.4112 18.8879H18.41Z"
                                        fill="#202A43"
                                      />
                                      <path
                                        d="M15.5929 11.0501C15.1398 10.9852 14.7131 10.6117 14.5339 10.2237C14.1418 9.37421 14.6175 8.34529 15.5922 8.14099C15.5817 7.85867 15.5387 7.35963 15.9482 7.32487C16.4615 7.28125 16.4401 7.79447 16.4201 8.14099C16.8804 8.2098 17.3173 8.59498 17.4904 8.99258C17.5525 9.13481 17.6334 9.4299 17.6229 9.57957C17.5996 9.91404 17.2706 10.0839 16.9688 9.91049C16.7502 9.78493 16.8307 9.45756 16.728 9.25114C16.6693 9.13303 16.5526 9.0018 16.4194 8.95711V10.4113C16.8725 10.4762 17.2992 10.8497 17.4784 11.2377C17.8705 12.0872 17.3948 13.1161 16.4201 13.3204C16.4307 13.6027 16.4736 14.1017 16.0641 14.1365C15.5508 14.1801 15.5722 13.6669 15.5922 13.3204C15.1319 13.2516 14.695 12.8664 14.5219 12.4688C14.4598 12.3266 14.3789 12.0315 14.3894 11.8818C14.4128 11.5473 14.7417 11.3774 15.0435 11.5509C15.2621 11.6764 15.1816 12.0038 15.2843 12.2102C15.3431 12.3283 15.4597 12.4596 15.5929 12.5043V11.0501ZM15.5929 10.2343V8.95747C15.0781 9.19297 15.08 10.0006 15.5929 10.2343ZM16.4194 12.5043C16.9342 12.2688 16.9323 11.4611 16.4194 11.2274V12.5043Z"
                                        fill="#202A43"
                                      />
                                      <path
                                        d="M7.54351 9.21182L13.2454 9.20508C13.7327 9.32283 13.7338 9.86691 13.2454 9.98537L7.57437 9.98466C7.06857 9.9233 7.05766 9.28949 7.54351 9.21182Z"
                                        fill="#202A43"
                                      />
                                      <path
                                        d="M7.54351 13.7528L13.2454 13.7461C13.7327 13.8638 13.7338 14.4079 13.2454 14.5264L7.57437 14.5257C7.06857 14.4643 7.05766 13.8305 7.54351 13.7528Z"
                                        fill="#202A43"
                                      />
                                      <path
                                        d="M7.46748 10.7361L13.283 10.7305C13.6913 10.8493 13.7214 11.327 13.3214 11.476L7.49872 11.4753C7.08776 11.3508 7.08325 10.8989 7.46748 10.7365V10.7361Z"
                                        fill="#202A43"
                                      />
                                      <path
                                        d="M7.46749 12.2626L13.3214 12.2559C13.7226 12.4055 13.6902 12.8826 13.283 13.0014H7.49872C7.10207 12.8741 7.07008 12.4179 7.46749 12.2626Z"
                                        fill="#202A43"
                                      />
                                      <path
                                        d="M7.46749 7.72247L10.5038 7.7168C10.9158 7.82959 10.8842 8.37969 10.4661 8.46162H7.49872C7.10207 8.33429 7.07008 7.87818 7.46749 7.72283V7.72247Z"
                                        fill="#202A43"
                                      />
                                    </svg>
                                  ) : (
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="24"
                                      height="25"
                                      viewBox="0 0 24 25"
                                      fill="none"
                                    >
                                      <rect
                                        width="24"
                                        height="25"
                                        rx="4"
                                        fill="#202A43"
                                      />
                                      <path
                                        d="M5.57818 5.05787C6.21891 4.99129 7.39636 4.97899 8.03564 5.03761C8.84291 5.11142 9.45236 5.6614 9.59418 6.4538V19.5462C9.46254 20.3024 8.87345 20.869 8.10255 20.9566C7.38255 21.0383 5.78473 21.0036 5.08436 20.8552C4.54836 20.7416 4.06909 20.0723 4 19.5469V6.4538C4.13018 5.85968 4.52073 5.28148 5.13964 5.12806C5.24364 5.10201 5.46545 5.06945 5.57818 5.05787ZM5.30618 7.93439H8.25164C8.40073 7.93439 8.40073 7.42784 8.25164 7.42784H5.34255C5.17164 7.42784 5.25673 7.82078 5.30618 7.93439ZM6.52145 16.1993C5.66545 16.3462 5.31127 17.4599 5.85673 18.1336C6.49309 18.9195 7.86109 18.6061 7.99491 17.5923C8.11491 16.6848 7.42764 16.0437 6.52218 16.1993H6.52145Z"
                                        fill="white"
                                      />
                                      <path
                                        d="M10.397 17.7771V16.1851H18.397V8.22485H10.397V6.63281H18.6515C18.6719 6.63281 18.9664 6.73774 19.0304 6.76235C19.6086 6.98451 19.8762 7.46501 19.9933 8.04756L19.9999 16.2241C19.9664 16.6779 19.7562 17.2177 19.3679 17.4768C19.2631 17.547 18.7424 17.7764 18.6508 17.7764H10.3962L10.397 17.7771Z"
                                        fill="white"
                                      />
                                      <path
                                        d="M14.397 18.5723V19.3683H16.797V20.9603H10.397V19.3683L11.197 19.369V18.5723H14.397Z"
                                        fill="white"
                                      />
                                    </svg>
                                  )
                                ) : null}
                              </div>
                            </button>
                            <div className="flex-space-100">
                              <div>Created At</div>
                              <div className=" font-[500]">
                                {formatDate(
                                  `rate` in transaction
                                    ? transaction.rate.created_at
                                    : ""
                                )}
                              </div>
                            </div>
                            {transaction.status != "completed" && (
                              <div className="flex-space-100">
                                <div>Expires At</div>
                                <div className=" font-[500]">
                                  {formatDate(
                                    `rate` in transaction
                                      ? transaction.rate.expires_at
                                      : ""
                                  )}
                                </div>
                              </div>
                            )}
                            <div className="flex-space-100">
                              <div>Exchange Rate</div>
                              <div className=" font-[500]">
                                {"1"}{" "}
                                {`rate` in transaction
                                  ? transaction.rate.from_currency_symbol
                                  : ""}{" "}
                                ={" "}
                                {`rate` in transaction
                                  ? transaction.rate.rate
                                  : ""}{" "}
                                {`rate` in transaction
                                  ? transaction.rate.to_currency_symbol
                                  : ""}
                              </div>
                            </div>
                            <div className="flex-space-100">
                              <div>Fee</div>
                              <div className=" font-[500]">
                                {Number(
                                  `rate` in transaction
                                    ? transaction.rate.fee.amount_percentage ??
                                        0
                                    : 0
                                ).toFixed(2)}
                                %
                              </div>
                            </div>
                            <div className="flex-space-100">
                              <div>Amount</div>
                              <div className=" font-[500]">
                                {amount.toFixed(2)}{" "}
                                {`rate` in transaction
                                  ? transaction.rate.from_currency_symbol
                                  : ""}{" "}
                              </div>
                            </div>

                            <div className="flex-space-100">
                              <div>Exchanged</div>
                              <div className=" font-[500]">
                                {`rate` in transaction
                                  ? transaction.rate.exchanged_amount
                                  : ""}{" "}
                                {`rate` in transaction
                                  ? transaction.rate.to_currency_symbol
                                  : ""}
                              </div>
                            </div>
                            <div
                              className={`flex-space-100 font-bold ${
                                `rate` in transaction &&
                                transaction.payment_status != null
                                  ? ""
                                  : "mb-4"
                              } `}
                            >
                              <div>Estimated total proceeds</div>
                              <div>
                                {`rate` in transaction
                                  ? transaction.rate.total
                                  : ""}
                              </div>
                            </div>
                            {`rate` in transaction ? (
                              transaction.payment_status != null ? (
                                <div className="flex-space-100 mb-4">
                                  <div>Card payment status</div>
                                  <div className=" font-[500]">
                                    {`rate` in transaction
                                      ? transaction.payment_status
                                      : ""}
                                  </div>
                                </div>
                              ) : null
                            ) : null}
                            {`rate` in transaction &&
                              transaction.admin_notes != "" && (
                                <div className="flex flex-col gap-2">
                                  {"rate" in transaction && (
                                    <textarea
                                      className="border border-[#D4D4D4] rounded-md p-2 text-sm resize-none"
                                      name="admin_notes"
                                      value={
                                        `rate` in transaction
                                          ? transaction.admin_notes
                                          : ""
                                      }
                                      rows={4}
                                      placeholder="Additional notes"
                                      disabled
                                    />
                                  )}
                                </div>
                              )}
                            {isBitramp() ? (
                              <div className="w-full ml-auto h-[1px] bg-[#464646] rounded-lg"></div>
                            ) : (
                              <div className="w-full ml-auto h-[1px] bg-[#0000001a] rounded-lg"></div>
                            )}
                            {`rate` in transaction && (
                              <div
                                onClick={() =>
                                  `rate` in transaction
                                    ? transaction.client != null
                                      ? handleDownloadReceipt()
                                      : handleDownloadInvoice()
                                    : null
                                }
                                className="flex justify-end items-center gap-2 cursor-pointer mt-4"
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="14"
                                  height="18"
                                  viewBox="0 0 14 18"
                                  fill="none"
                                >
                                  <g clip-path="url(#clip0_3969_7933)">
                                    <path
                                      d="M14.0002 8.01575L6.9361 14.6214L0.00168819 7.93497C-0.0543177 7.69687 1.30014 6.61413 1.54038 6.3321L5.8322 10.2649V0H8.16676V10.2649L12.3348 6.3321L14.0017 7.73372V8.01433L14.0002 8.01575Z"
                                      fill="#737791"
                                    />
                                    <path
                                      d="M13.4177 15.75H0.582031V18.0005H13.4177V15.75Z"
                                      fill="#737791"
                                    />
                                  </g>
                                  <defs>
                                    <clipPath id="clip0_3969_7933">
                                      <rect
                                        width="14"
                                        height="18"
                                        fill="white"
                                      />
                                    </clipPath>
                                  </defs>
                                </svg>
                                <div className="text-right text-[#737791] font-bold">
                                  Download
                                </div>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            ) : (
              <tr>
                <td colSpan={5}>No transactions found</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TableTry;
